import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, FileUp } from "lucide-react";

const CAT_LABELS = {
  starter: "Entrant",
  main: "Principal",
  dessert: "Postres",
  drink: "Beguda",
  side: "Guarnició",
  sauce: "Salsa",
  bread: "Pa",
  other: "Altres",
};

const CAT_COLORS = {
  starter: "bg-amber-100 text-amber-700",
  main: "bg-red-100 text-red-700",
  dessert: "bg-pink-100 text-pink-700",
  drink: "bg-blue-100 text-blue-700",
  side: "bg-green-100 text-green-700",
  sauce: "bg-orange-100 text-orange-700",
  bread: "bg-yellow-100 text-yellow-700",
  other: "bg-gray-100 text-gray-700",
};

export default function CsvImportModal({ open, onClose, venueId, onImported }) {
  const [step, setStep] = useState("input"); // "input" | "review" | "done"
  const [csvFile, setCsvFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({});
  const [savedCount, setSavedCount] = useState(0);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setParseError(null);
    }
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ""));
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim().replace(/^"|"$/g, ""));
    return result;
  };

  const handleParse = async () => {
    if (!csvFile) return;
    setParsing(true);
    setParseError(null);
    try {
      const text = await csvFile.text();
      const lines = text.trim().split("\n").filter(l => l.trim());
      if (lines.length < 2) {
        setParseError("El CSV ha de tenir capçalera i almenys una fila de dades.");
        setParsing(false);
        return;
      }

      const headerLine = parseCSVLine(lines[0]);
      const headers = headerLine.map(h => h.toLowerCase().trim());
      const nameIdx = headers.findIndex(h => h === "name" || h === "nom");
      const categoryIdx = headers.findIndex(h => h === "category" || h === "categoria");
      const descriptionIdx = headers.findIndex(h => h === "description" || h === "descripció");
      const priceIdx = headers.findIndex(h => h === "selling_price" || h === "price");
      const allergensIdx = headers.findIndex(h => h === "allergens" || h === "al·lèrgens");
      const costIdx = headers.findIndex(h => h === "estimated_cost" || h === "cost");

      if (nameIdx === -1) {
        setParseError('El CSV ha de tenir una columna "name" o "nom" (obligatori).');
        setParsing(false);
        return;
      }

      const parsed = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (!values[nameIdx] || !values[nameIdx].trim()) continue;

        const dish = {
          name: values[nameIdx],
          category: categoryIdx !== -1 && values[categoryIdx] ? values[categoryIdx].toLowerCase() : "other",
          description: descriptionIdx !== -1 ? values[descriptionIdx] : "",
          selling_price: priceIdx !== -1 && values[priceIdx] ? parseFloat(values[priceIdx]) : undefined,
          estimated_cost: costIdx !== -1 && values[costIdx] ? parseFloat(values[costIdx]) : undefined,
          allergens: allergensIdx !== -1 && values[allergensIdx] ? values[allergensIdx].split(";").map(a => a.trim()).filter(a => a) : [],
        };
        parsed.push(dish);
      }

      if (parsed.length === 0) {
        setParseError("No s'han detectat plats vàlids al CSV.");
        setParsing(false);
        return;
      }

      setDishes(parsed);
      const sel = {};
      parsed.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setStep("review");
    } catch (err) {
      setParseError("Error llegint el CSV: " + (err?.message || "Error desconegut"));
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    setSaving(true);
    const toImport = dishes.filter((_, i) => selected[i]);
    try {
      const batchSize = 50;
      let count = 0;
      for (let i = 0; i < toImport.length; i += batchSize) {
        const batch = toImport.slice(i, i + batchSize);
        await hosys.entities.RecipeSheet.bulkCreate(
          batch.map(d => ({
            venue_id: venueId,
            name: d.name,
            category: d.category || "other",
            description: d.description || "",
            selling_price: d.selling_price || undefined,
            estimated_cost: d.estimated_cost || undefined,
            allergens: d.allergens || [],
            ingredients: [],
          }))
        );
        count += batch.length;
      }
      setSavedCount(count);
      setStep("done");
      onImported();
    } catch (err) {
      setParseError("Error important plats: " + (err?.message || "Error desconegut"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep("input");
    setCsvFile(null);
    setDishes([]);
    setSelected({});
    setParseError(null);
    setSavedCount(0);
    onClose();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5 text-[#7B2D3F]" />
            Importar CSV
          </DialogTitle>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Carrega un fitxer CSV amb les columnes: <strong>name, category, description, selling_price, estimated_cost, allergens</strong>
            </p>

            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
              <input type="file" accept=".csv" id="csv-upload" className="hidden" onChange={handleFileSelect} />
              {csvFile ? (
                <div className="space-y-2">
                  <p className="text-2xl">📊</p>
                  <p className="font-medium text-gray-900">{csvFile.name}</p>
                  <p className="text-xs text-gray-400">{(csvFile.size / 1024).toFixed(0)} KB</p>
                  <button onClick={() => setCsvFile(null)} className="text-xs text-red-500 underline">Eliminar</button>
                </div>
              ) : (
                <label htmlFor="csv-upload" className="cursor-pointer space-y-2 block">
                  <p className="text-3xl">📊</p>
                  <p className="text-sm font-medium text-gray-700">Fes clic per seleccionar un CSV</p>
                  <p className="text-xs text-gray-400">Fitxer CSV (màx. 10 MB)</p>
                </label>
              )}
            </div>

            {parseError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                ⚠️ {parseError}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 space-y-1 font-mono text-[11px]">
              <p><strong>Exemple de CSV:</strong></p>
              <p>name,category,description,selling_price,estimated_cost,allergens</p>
              <p>"Pizza Margarita","main","Tomàquet i mozzarella",12.00,3.50,"Làctics"</p>
              <p>"Amanida","starter","Variada",8.50,2.00,""</p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel·lar</Button>
              <Button
                onClick={handleParse}
                disabled={!csvFile || parsing}
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
              >
                {parsing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processant...</>
                ) : (
                  <>Analitzar CSV</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                S'han detectat <strong>{dishes.length} plats</strong>. Selecciona els que vols importar.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { const s = {}; dishes.forEach((_, i) => { s[i] = true; }); setSelected(s); }}
                  className="text-xs text-[#7B2D3F] underline"
                >
                  Tots
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setSelected({})}
                  className="text-xs text-gray-500 underline"
                >
                  Cap
                </button>
              </div>
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {dishes.map((dish, i) => (
                <div
                  key={`dish-${i}`}
                  onClick={() => setSelected(prev => ({ ...prev, [i]: !prev[i] }))}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    selected[i]
                      ? "border-[#7B2D3F] bg-[#7B2D3F]/5"
                      : "border-gray-200 bg-white opacity-50"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selected[i] ? "border-[#7B2D3F] bg-[#7B2D3F]" : "border-gray-300"
                  }`}>
                    {selected[i] && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm">{dish.name}</p>
                      <Badge variant="secondary" className={`text-[10px] border-0 ${CAT_COLORS[dish.category] || CAT_COLORS.other}`}>
                        {CAT_LABELS[dish.category] || "Altres"}
                      </Badge>
                    </div>
                    {dish.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{dish.description}</p>
                    )}
                    {dish.allergens?.length > 0 && (
                      <p className="text-xs text-red-500 mt-0.5">⚠ {dish.allergens.join(", ")}</p>
                    )}
                  </div>
                  {dish.selling_price && (
                    <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                      {dish.selling_price.toFixed(2)}€
                    </span>
                  )}
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("input")}>Tornar</Button>
              <Button
                onClick={handleImport}
                disabled={selectedCount === 0 || saving}
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Important...</>
                ) : (
                  <>Importar {selectedCount} plats</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "done" && (
          <div className="py-10 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Importació completada</h3>
            <p className="text-sm text-gray-500">
              S'han creat <strong>{savedCount} fitxes tècniques</strong> correctament.<br />
              Ara pots editar cada una per afegir més detalls.
            </p>
            <Button onClick={handleClose} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] mt-2">
              Tancar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}